<template>
  <main class="wecom-entry">
    <div class="status" role="status" aria-live="polite">
      <span v-if="loading" class="spinner" aria-hidden="true"></span>
      <Icon v-else icon="lucide:circle-alert" :size="28" aria-hidden="true" />
      <h1>{{ loading ? '正在进入私域管理' : '暂时无法登录' }}</h1>
      <p v-if="errorMessage">{{ errorMessage }}</p>
      <button v-if="!loading" type="button" @click="startAuthorization">
        <Icon icon="lucide:refresh-cw" :size="17" />
        重试
      </button>
    </div>
  </main>
</template>

<script lang="ts" setup>
import { Icon } from '@/components/Icon'
import * as LoginApi from '@/api/login'
import * as authUtil from '@/utils/auth'

defineOptions({ name: 'WeComEntry' })

const route = useRoute()
const loading = ref(true)
const errorMessage = ref('')
const WE_COM_SOCIAL_TYPE = 30
const SCRM_CENTER_PREFIX = '/ai-business-center/scrm'
const DEFAULT_REDIRECT = `${SCRM_CENTER_PREFIX}/dashboard?standalone=1&app=scrm`

const firstQueryValue = (value: unknown) => (Array.isArray(value) ? value[0] : value)

const getSafeRedirect = () => {
  const redirect = firstQueryValue(route.query.redirect)
  if (typeof redirect !== 'string' || !redirect.startsWith('/') || redirect.startsWith('//')) {
    return DEFAULT_REDIRECT
  }

  const target = new URL(redirect, location.origin)
  if (target.pathname === '/scrm' || target.pathname.startsWith('/scrm/')) {
    target.pathname = `/ai-business-center${target.pathname}`
  }
  if (
    target.pathname === SCRM_CENTER_PREFIX ||
    target.pathname.startsWith(`${SCRM_CENTER_PREFIX}/`)
  ) {
    target.searchParams.set('standalone', '1')
    target.searchParams.set('app', 'scrm')
  }
  return `${target.pathname}${target.search}${target.hash}`
}

const resolveTenantId = async () => {
  const tenantId = firstQueryValue(route.query.tenantId)
  if (typeof tenantId === 'string' && /^\d+$/.test(tenantId) && tenantId !== '0') {
    return tenantId
  }

  const tenantName = firstQueryValue(route.query.tenantName)
  if (typeof tenantName === 'string' && tenantName.trim()) {
    return String(await LoginApi.getTenantIdByName(tenantName.trim()))
  }

  const tenant = await LoginApi.getTenantByWebsite(location.host)
  if (tenant?.id) return String(tenant.id)

  const cachedTenantId = authUtil.getTenantId()
  const defaultTenantId = import.meta.env.VITE_APP_DEFAULT_TENANT_ID
  return String(cachedTenantId || defaultTenantId || '')
}

const startAuthorization = async () => {
  loading.value = true
  errorMessage.value = ''
  try {
    const tenantId = await resolveTenantId()
    if (!/^\d+$/.test(tenantId) || tenantId === '0') {
      throw new Error('未找到当前企业对应的租户配置')
    }
    authUtil.setTenantId(tenantId)

    const callbackQuery = new URLSearchParams({
      type: String(WE_COM_SOCIAL_TYPE),
      redirect: getSafeRedirect(),
      tenantId
    })
    const redirectUri = `${location.origin}/social-login?${encodeURIComponent(callbackQuery.toString())}`
    const authorizeUrl = await LoginApi.socialAuthRedirect(
      WE_COM_SOCIAL_TYPE,
      encodeURIComponent(redirectUri)
    )
    location.replace(authorizeUrl)
  } catch (error) {
    loading.value = false
    errorMessage.value = error instanceof Error ? error.message : '请检查企业微信应用配置'
  }
}

onMounted(startAuthorization)
</script>

<style lang="scss" scoped>
.wecom-entry {
  min-height: 100vh;
  display: grid;
  place-items: center;
  padding: 24px;
  background: #f5f7fa;
  color: #1f2937;
}

.status {
  width: min(100%, 360px);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.status h1 {
  margin: 18px 0 0;
  font-size: 20px;
  line-height: 1.4;
  letter-spacing: 0;
}

.status p {
  margin: 10px 0 0;
  color: #667085;
  font-size: 14px;
  line-height: 1.6;
}

.status button {
  min-height: 40px;
  margin-top: 22px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 18px;
  border: 0;
  border-radius: 6px;
  background: #2563eb;
  color: #fff;
  cursor: pointer;
  font-size: 14px;
}

.spinner {
  width: 30px;
  height: 30px;
  border: 3px solid #dbe3ef;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
